import { authUser } from '../constants/common'
import { StorageIds } from '../constants/storage'
import { UserResponseDataType } from '../types/servicesTypes/identityTypes'

/**
 * Retrieves the active user storage from localStorage.
 *
 * @return {string} The active user storage, or an empty string if not found.
 */
export const getActiveUserStorage = (): string => {
  const authenticatedUserType = hasRegisteredUser() ? authUser.REGISTERED : authUser.ANONYMOUS

  return localStorage.getItem(authenticatedUserType) || '{}'
}

/**
 * Retrieves the active user from localStorage.
 *
 * @returns {UserResponseDataType | null} The active user, or null if not found.
 */
export const getActiveUser = (): UserResponseDataType | null => {
  const authenticatedUserType = hasRegisteredUser() ? authUser.REGISTERED : authUser.ANONYMOUS
  const authenticatedUserStorage = localStorage.getItem(authenticatedUserType)

  if (!authenticatedUserStorage) {
    return null
  }

  try {
    return JSON.parse(authenticatedUserStorage) as UserResponseDataType
  } catch (error) {}

  return null
}

/**
 * Retrieves the registered user from localStorage.
 *
 * @returns The registered user, or an empty string if not found.
 */
export const getRegisteredUser = () => {
  return localStorage.getItem(StorageIds.REGISTERED) || '{}'
}

/**
 * Retrieves the anonymous user from localStorage.
 *
 * @return {string} The anonymous user, or an empty string if not found.
 */
export const getAnonymousUser = (): string => {
  return localStorage.getItem(StorageIds.ANONYMOUS) || '{}'
}

/**
 * Checks if there is an anonymous user in localStorage.
 *
 * @return {boolean} Returns true if there is an anonymous user, false otherwise.
 */
export const hasAnonymousUser = () => {
  return Boolean(localStorage.getItem(StorageIds.ANONYMOUS))
}

/**
 * Checks if there is a registered user in localStorage.
 *
 * @return {boolean} Returns true if there is a registered user, false otherwise.
 */
export const hasRegisteredUser = () => {
  return Boolean(localStorage.getItem(StorageIds.REGISTERED))
}

export const getUserType = () => {
  if (hasRegisteredUser()) {
    return authUser.REGISTERED
  } else if (hasAnonymousUser()) {
    return authUser.ANONYMOUS
  }
}

/**
 * Updates the anonymous user in localStorage with the provided data.
 *
 * @param {UserResponseDataType} data - The data to update the anonymous user with.
 * @return {void} This function does not return anything.
 */
export const updateAnonymousUser = (data: UserResponseDataType): void => {
  const patch = { ...JSON.parse(getAnonymousUser()), ...data }
  localStorage.setItem(StorageIds.ANONYMOUS, JSON.stringify({ ...patch }))
}

/**
 * Updates the registered user in localStorage with the provided data.
 *
 * @param {UserResponseDataType} data - The data to update the registered user with.
 * @return {void} This function does not return anything.
 */
export const updateRegisteredUser = (data: UserResponseDataType): void => {
  const patch = { ...JSON.parse(getRegisteredUser()), ...data }
  localStorage.setItem(StorageIds.REGISTERED, JSON.stringify({ ...patch }))
}

/**
 * Clears the anonymous user storage by removing the item with the key `StorageIds.ANONYMOUS` from the localStorage.
 *
 * @return {void} This function does not return anything.
 */
export const clearAnonymousUserStorage = () => {
  localStorage.removeItem(StorageIds.ANONYMOUS)
}

/**
 * Clears the registered user storage by removing the item with the key `StorageIds.REGISTERED` from the localStorage.
 *
 * @return {void} This function does not return anything.
 */
export const clearRegisteredUserStorage = () => {
  localStorage.removeItem(StorageIds.REGISTERED)
}

/**
 * Clears the users storage by removing the items with the keys `StorageIds.ANONYMOUS` and `StorageIds.REGISTERED` from the localStorage.
 *
 * @return {void} This function does not return anything.
 */
export const clearUsersStorage = () => {
  clearAnonymousUserStorage()
  clearRegisteredUserStorage()
}
